import { Loader2, Ticket, Plus, Trash2, RotateCcw } from "lucide-react";
import { TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { StatusPill } from "@/components/admin/widgets";

export const CouponsPanel = ({
  coupons,
  newCoupon,
  setNewCoupon,
  creatingCoupon,
  onCreateCoupon,
  onDeleteCoupon,
  onReinstateCoupon,
}) => (
  <TabsContent value="coupons" className="mt-6 space-y-6" data-testid="coupons-panel">
    <div className="bg-white border border-border rounded-2xl p-8">
      <div className="flex items-center gap-2 text-ink">
        <Ticket className="h-5 w-5 text-clay" />
        <h2 className="font-heading text-xl">Create a coupon</h2>
      </div>
      <p className="mt-2 text-sm text-ink-muted max-w-2xl">
        Coupons give a discount on the unlock price. A 100% coupon unlocks instantly
        without payment. Each coupon can be used once. During a sale, normal coupons are
        paused (only a 100%-off code or the automatic returning-customer reward still apply).
      </p>
      <div className="mt-5 grid gap-4 sm:grid-cols-3">
        <div>
          <Label className="text-ink font-medium">Code</Label>
          <Input
            data-testid="coupon-code-input"
            value={newCoupon.code}
            onChange={(e) => setNewCoupon((c) => ({ ...c, code: e.target.value.toUpperCase() }))}
            placeholder="e.g. WELCOME25"
            className="mt-2 bg-white border-border focus-visible:ring-clay rounded-xl py-6 uppercase tracking-wide"
          />
        </div>
        <div>
          <Label className="text-ink font-medium">Discount %</Label>
          <Input
            type="number"
            min={1}
            max={100}
            data-testid="coupon-discount-input"
            value={newCoupon.discount_percent}
            onChange={(e) => setNewCoupon((c) => ({ ...c, discount_percent: e.target.value }))}
            className="mt-2 bg-white border-border focus-visible:ring-clay rounded-xl py-6"
          />
        </div>
        <div>
          <Label className="text-ink font-medium">Expires</Label>
          <Input
            type="date"
            data-testid="coupon-expiry-input"
            value={newCoupon.expires_at}
            onChange={(e) => setNewCoupon((c) => ({ ...c, expires_at: e.target.value }))}
            className="mt-2 bg-white border-border focus-visible:ring-clay rounded-xl py-6"
          />
        </div>
      </div>
      <div className="mt-5 flex justify-end">
        <Button
          data-testid="create-coupon-button"
          onClick={onCreateCoupon}
          disabled={creatingCoupon || !newCoupon.code.trim() || !newCoupon.expires_at}
          className="bg-clay hover:bg-clay-hover text-white rounded-lg px-7 py-5 font-medium"
        >
          {creatingCoupon ? <Loader2 className="h-4 w-4 animate-spin" /> : (<><Plus className="h-4 w-4 mr-2" /> Create coupon</>)}
        </Button>
      </div>
    </div>

    <div className="bg-white border border-border rounded-2xl p-8" data-testid="coupons-list">
      <h2 className="font-heading text-xl text-ink mb-4">All coupons</h2>
      {coupons.length === 0 ? (
        <p className="text-sm text-ink-muted">No coupons yet.</p>
      ) : (
        <div className="divide-y divide-border">
          {coupons.map((c) => (
            <div key={c.code} className="flex items-center justify-between gap-4 py-3" data-testid={`coupon-row-${c.code}`}>
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <code className="font-mono font-semibold text-ink tracking-wide">{c.code}</code>
                  <span className="text-sm text-ink-muted">{c.discount_percent}% off</span>
                  {c.auto_generated && (
                    <span className="text-[10px] uppercase tracking-wide text-forest bg-forest/10 px-1.5 py-0.5 rounded-full">auto</span>
                  )}
                </div>
                <div className="text-xs text-ink-faint mt-0.5">
                  Expires {c.expires_at ? new Date(c.expires_at).toLocaleDateString() : "—"}
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <StatusPill status={c.status} />
                {c.status === "used" && (
                  <Button variant="ghost" size="sm" data-testid={`reinstate-${c.code}`} onClick={() => onReinstateCoupon(c.code)} className="text-ink-muted hover:text-ink">
                    <RotateCcw className="h-4 w-4" />
                  </Button>
                )}
                <Button variant="ghost" size="sm" data-testid={`delete-${c.code}`} onClick={() => onDeleteCoupon(c.code)} className="text-clay hover:text-clay-hover">
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  </TabsContent>
);
